import { Injectable, signal, computed, inject } from '@angular/core';
import { finalize, take, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  WalletResponseDto,
  RegistryTypeDto,
} from 'src/generated-sources/openapi/wallet-service/wallet';
import {
  AlertService,
  AlertReportDetailDto,
} from 'src/generated-sources/openapi/alert-service/alert';
import { PartnerShareholdingResponseDto } from 'src/generated-sources/openapi/alert-service/registration-data/model/partner-shareholding-response';
import { PartnersShareholdingRequestDto } from 'src/generated-sources/openapi/alert-service/registration-data/model/partners-shareholding-request';
import { PartnersRegistrationDataRequestDto } from 'src/generated-sources/openapi/alert-service/registration-data/model/partners-registration-data-request';
import { PartnersRegistrationDataResponseDto } from 'src/generated-sources/openapi/alert-service/registration-data/model/partners-registration-data-response';
import { PlanIndicatorService } from 'src/generated-sources/openapi/configuration-service/plan-indicator';
import { DateUtilsService } from 'src/app/services/date-utils.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import {
  AlertIndicatorVariation,
  FlattenedIndicator,
  PaginationState,
  WalletRegistryAlertResponse,
} from './models/wallet-alert-report.models';
import {
  ALL_INDICATORS_OPTION,
  LOADING_INDICATORS_OPTION,
  NO_INDICATORS_OPTION,
  DEFAULT_LAST_PROCESSING_DATE,
  DEFAULT_PER_PAGE,
  UNAVAILABLE_DATE,
} from './models/wallet-alert-report.constants';
import { RegistrationDataLoaderService } from './services/registration-data-loader.service';
import { AlertDataTransformerService } from './services/alert-data-transformer.service';
import { AlertRequestBuilderService } from './services/alert-request-builder.service';
import { IndicatorDataService } from 'src/app/services/data/indicator-data.service';

@Injectable()
export class WalletAlertReportFacade {
  private readonly alertService = inject(AlertService);
  private readonly planIndicatorService = inject(PlanIndicatorService);
  private readonly indicatorDataService = inject(IndicatorDataService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly registrationDataLoaderService = inject(RegistrationDataLoaderService);
  private readonly transformer = inject(AlertDataTransformerService);
  private readonly requestBuilder = inject(AlertRequestBuilderService);

  // === SIGNALS: Reactive State ===
  readonly indicators = signal<FlattenedIndicator[]>([]);
  readonly alerts = signal<WalletRegistryAlertResponse[]>([]);
  readonly lastProcessingDate = signal<string>(DEFAULT_LAST_PROCESSING_DATE);
  readonly hasError = signal<boolean>(false);
  readonly hasNoIndicators = signal<boolean>(false);
  readonly isLoadingAlerts = signal<boolean>(false);
  readonly isLoadingIndicators = signal<boolean>(false);

  readonly pagination = signal<PaginationState>({
    page: 0,
    perPage: DEFAULT_PER_PAGE,
    totalRegisters: 0,
  });

  // === COMPUTED SIGNALS ===
  readonly isLoading = computed(() => this.isLoadingAlerts() || this.isLoadingIndicators());

  readonly isIndicatorSelectDisabled = computed(() => this.isLoading() || this.hasNoIndicators());

  /**
   * Loads and flattens indicators for a plan, grouping child indicators by their `group` property.
   * Shows ALL_INDICATORS_OPTION as first item when indicators exist.
   */
  loadIndicators(planId: string, walletType: RegistryTypeDto, onLoaded?: () => void): void {
    this.isLoadingIndicators.set(true);
    this.indicators.set([LOADING_INDICATORS_OPTION]);

    this.planIndicatorService
      .findByPlanIdGrouped({ planId })
      .pipe(
        take(1),
        finalize(() => this.isLoadingIndicators.set(false)),
      )
      .subscribe({
        next: (response) => {
          const sortedParents = this.indicatorDataService
            .sortByDisplayOrder(response.items || [], (item) => item.id)
            .map((parent) => ({
              ...parent,
              planIndicators: this.indicatorDataService.sortByDisplayOrder(
                parent.planIndicators || [],
                (pi) => pi.indicator.id,
              ),
            }));

          const flattenedIndicators = this.transformer.flattenIndicatorHierarchy(
            sortedParents,
            walletType,
          );

          if (flattenedIndicators.length === 0) {
            this.indicators.set([NO_INDICATORS_OPTION]);
            this.hasNoIndicators.set(true);
            this.clearAlerts();
          } else {
            this.indicators.set([ALL_INDICATORS_OPTION, ...flattenedIndicators]);
            this.hasNoIndicators.set(false);
          }
          onLoaded?.();
        },
        error: (error) => {
          console.error('Error fetching indicators:', error);
          this.indicators.set([NO_INDICATORS_OPTION]);
          this.hasNoIndicators.set(true);
          this.clearAlerts();
          this.snackbarService.error(
            $localize`:@@js_getIndicatorsListError:Error al intentar obtener la lista de indicadores`,
          );
        },
      });
  }

  /**
   * Loads paginated alerts with optional filters.
   *
   * Special handling:
   * - ALL_INDICATORS_OPTION: Fetches all alerts (no indicatorId filter)
   * - document: Validates as Brazilian CPF/CNPJ before sending
   */
  loadAlerts(params: {
    wallet: WalletResponseDto;
    indicator: FlattenedIndicator;
    document: string;
    page: number;
  }): void {
    const { wallet, indicator, document, page } = params;

    if (!wallet) return;

    this.isLoadingAlerts.set(true);
    this.hasError.set(false);

    const requestParams = this.requestBuilder.buildListWalletAlertsParams(
      wallet.id,
      indicator,
      document,
      page,
    );

    this.alertService
      .listWalletAlertsGrouped(requestParams)
      .pipe(finalize(() => this.isLoadingAlerts.set(false)))
      .subscribe({
        next: (response) => {
          this.hasError.set(false);
          this.alerts.set(
            response.registries.map((r) => ({
              ...r,
              _combinedAlerts: this.transformer.buildCombinedAlerts(r),
            })),
          );

          const lastProcessingDate = response?.registries.length
            ? this.dateUtilsService.formatDate(response.processingDate)
            : UNAVAILABLE_DATE;
          this.lastProcessingDate.set(lastProcessingDate);

          this.pagination.set({
            page,
            perPage: DEFAULT_PER_PAGE,
            totalRegisters: response.total,
          });
        },
        error: () => {
          this.hasError.set(true);
          this.alerts.set([]);
          this.lastProcessingDate.set(UNAVAILABLE_DATE);
          this.pagination.set({
            page: 0,
            perPage: DEFAULT_PER_PAGE,
            totalRegisters: 0,
          });
          this.snackbarService.error(
            $localize`:@@js_getWalletAlertsList:Error al intentar obtener la lista de alertas`,
          );
        },
      });
  }

  clearAlerts(): void {
    this.alerts.set([]);
    this.lastProcessingDate.set(DEFAULT_LAST_PROCESSING_DATE);
  }

  getDefaultIndicator(): FlattenedIndicator | undefined {
    return this.indicators()[0];
  }

  isParentIndicator(indicator: FlattenedIndicator): boolean {
    return indicator.isParentIndicator;
  }

  getAlertVariationCards(combinedAlerts: AlertReportDetailDto[]): AlertIndicatorVariation[] {
    return this.mapToAlertIndicatorVariation(combinedAlerts);
  }

  mapToAlertIndicatorVariation(data: AlertReportDetailDto[]): AlertIndicatorVariation[] {
    if (!data?.length) {
      return [];
    }

    return this.transformer.mapToAlertIndicatorVariation(data);
  }

  buildPartnersWithFlags(
    alerts: AlertReportDetailDto[],
  ): ReturnType<AlertDataTransformerService['buildPartnersWithFlags']> {
    return this.transformer.buildPartnersWithFlags(alerts);
  }

  parseShareholdingPartnerIds(currentValue: string | undefined): string[] {
    return this.transformer.parseShareholdingPartnerIds(currentValue);
  }

  loadPartnersRegistrationData(
    level: PartnersRegistrationDataRequestDto.LevelEnum,
    partners: PartnersRegistrationDataRequestDto['partners'],
  ): Observable<PartnersRegistrationDataResponseDto[] | null> {
    return this.registrationDataLoaderService
      .loadPartnersRegistrationData({ level, partners })
      .pipe(
        catchError(() => {
          this.snackbarService.error(
            $localize`:@@failGettingPartnersRegistrationData:Error al intentar recuperar los datos de registro de los socios.`,
          );
          return of(null);
        }),
      );
  }

  loadPartnersShareholding(
    rootRegistryId: string,
    level: PartnersShareholdingRequestDto.LevelEnum,
    partnerRegistryIds: string[],
  ): Observable<PartnerShareholdingResponseDto[] | null> {
    return this.registrationDataLoaderService
      .loadPartnersShareholding({
        rootCompanyRegistryId: rootRegistryId,
        level,
        partners: partnerRegistryIds,
      })
      .pipe(
        catchError(() => {
          this.snackbarService.error(
            $localize`:@@failGettingShareholding:Error al intentar recuperar el percentual societário.`,
          );
          return of(null);
        }),
      );
  }

  reset(): void {
    this.indicators.set([]);
    this.alerts.set([]);
    this.lastProcessingDate.set(DEFAULT_LAST_PROCESSING_DATE);
    this.hasError.set(false);
    this.hasNoIndicators.set(false);
    this.isLoadingAlerts.set(false);
    this.isLoadingIndicators.set(false);
    this.pagination.set({
      page: 0,
      perPage: DEFAULT_PER_PAGE,
      totalRegisters: 0,
    });
  }
}
